import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input,
  OnInit, Output, ViewChild
} from '@angular/core';
import {
  CHAT_EMOJI_CATEGORIES, CHAT_EMOJI_CATEGORY_ICONS, CHAT_EMOJI_LABELS_PT, ChatEmoji, ChatEmojiCategory,
  ChatEmojiCategoryId, ChatEmojiPickerLabels
} from '../../chat.emojis';

/** Um separador do seletor: as categorias de `chat.emojis.ts` mais "Recentes". */
export type ChatEmojiTab = 'recent' | ChatEmojiCategoryId;

const RECENT_MAX = 24;

/**
 * Seletor de emojis sem dependências (Angular 13 / TS 4.5, sem Bootstrap),
 * partilhado pelo CRM e pela PWA.
 *
 * - separadores por categoria (+ "Recentes", guardados em localStorage, com
 *   try/catch: em modo privado ou sem storage simplesmente não há recentes);
 * - pesquisa por nome em PT, sem acentos, sobre todas as categorias;
 * - teclado: setas movem-se na grelha, Home/End vão ao início/fim da linha,
 *   Enter/Espaço escolhem, Esc fecha; a pesquisa recebe o foco ao abrir e a
 *   seta para baixo passa para a grelha;
 * - `pick` emite o emoji e o painel FICA aberto (quem chama decide se fecha);
 *   `close` é emitido com Esc e com um clique fora do painel.
 *
 * As cores vêm de `--chat-*` quando existem, com valores neutros de recurso,
 * para o componente funcionar fora da página de chat.
 */
@Component({
  selector: 'chat-emoji-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-emoji-picker.component.html',
  styleUrls: ['./chat-emoji-picker.component.css']
})
export class ChatEmojiPickerComponent implements OnInit, AfterViewInit {
  @Input() labels: ChatEmojiPickerLabels = CHAT_EMOJI_LABELS_PT;
  /** Chave do localStorage dos recentes; a PWA e o CRM podem usar chaves diferentes. */
  @Input() storageKey = 'chat-emoji-recent';
  /** Colunas da grelha (as setas cima/baixo saltam este número de células). */
  @Input() columns = 8;
  /** Pôr o foco na pesquisa ao abrir. */
  @Input() autofocus = true;

  @Output() pick = new EventEmitter<string>();
  @Output() close = new EventEmitter<void>();

  @ViewChild('search', { static: true }) search!: ElementRef<HTMLInputElement>;
  @ViewChild('grid', { static: true }) grid!: ElementRef<HTMLElement>;

  readonly categories: ChatEmojiCategory[] = CHAT_EMOJI_CATEGORIES;
  readonly icons = CHAT_EMOJI_CATEGORY_ICONS;
  tabs: ChatEmojiTab[] = [];
  tab: ChatEmojiTab = 'smileys';
  query = '';
  /** O que a grelha mostra: a categoria ativa, os recentes ou o resultado da pesquisa. */
  visible: ChatEmoji[] = [];
  /** Índice da célula que tem o tabindex 0 (roving tabindex). */
  focusIndex = 0;
  recent: ChatEmoji[] = [];

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.recent = this.readRecent();
    const base: ChatEmojiTab[] = this.recent.length > 0 ? ['recent'] : [];
    this.tabs = base.concat(this.categories.map(c => c.id));
    this.tab = this.recent.length > 0 ? 'recent' : 'smileys';
    this.refresh();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.armed = true, 0);
    if (this.autofocus) {
      // Depois de o painel estar no ecrã (position: absolute já resolvido).
      setTimeout(() => this.search.nativeElement.focus(), 0);
    }
  }

  get title(): string {
    if (this.query.trim()) {
      return this.labels.title;
    }
    return this.tab === 'recent' ? this.labels.recent : (this.labels.categories[this.tab] || this.tab);
  }

  labelOf(tab: ChatEmojiTab): string {
    return tab === 'recent' ? this.labels.recent : (this.labels.categories[tab] || tab);
  }

  setTab(tab: ChatEmojiTab): void {
    this.tab = tab;
    this.query = '';
    this.search.nativeElement.value = '';
    this.refresh();
  }

  onSearch(event: Event): void {
    this.query = (event.target as HTMLInputElement).value || '';
    this.refresh();
  }

  clearSearch(): void {
    this.query = '';
    this.search.nativeElement.value = '';
    this.refresh();
    this.search.nativeElement.focus();
  }

  choose(emoji: ChatEmoji): void {
    this.rememberRecent(emoji);
    this.pick.emit(emoji.c);
  }

  trackByChar = (_i: number, e: ChatEmoji) => e.c;

  /* ---- teclado ---- */

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' && this.visible.length > 0) {
      event.preventDefault();
      this.focusCell(0);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.query) {
        this.clearSearch();
      } else {
        this.close.emit();
      }
    }
  }

  onGridKeydown(event: KeyboardEvent, index: number): void {
    const count = this.visible.length;
    if (count === 0) {
      return;
    }
    let next = index;
    switch (event.key) {
      case 'ArrowRight': next = Math.min(count - 1, index + 1); break;
      case 'ArrowLeft': next = Math.max(0, index - 1); break;
      case 'ArrowDown': next = index + this.columns < count ? index + this.columns : index; break;
      case 'ArrowUp':
        if (index - this.columns >= 0) {
          next = index - this.columns;
        } else {
          event.preventDefault();
          this.search.nativeElement.focus();
          return;
        }
        break;
      case 'Home': next = index - (index % this.columns); break;
      case 'End': next = Math.min(count - 1, index - (index % this.columns) + this.columns - 1); break;
      case 'Escape':
        event.preventDefault();
        this.close.emit();
        return;
      default:
        return;
    }
    event.preventDefault();
    this.focusCell(next);
  }

  /**
   * O clique no botão que abre o seletor ainda está a propagar-se quando este
   * listener nasce: sem isto o seletor fechava-se no mesmo instante em que abria.
   * Só se ouve o documento a partir do ciclo seguinte.
   */
  private armed = false;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.armed) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close.emit();
    }
  }

  private focusCell(index: number): void {
    this.focusIndex = index;
    this.cdr.markForCheck();
    const cells = this.grid.nativeElement.querySelectorAll<HTMLButtonElement>('button');
    const cell = cells[index];
    if (cell) {
      cell.focus();
    }
  }

  private refresh(): void {
    const q = normalize(this.query.trim());
    if (q) {
      const out: ChatEmoji[] = [];
      this.categories.forEach(cat => {
        const catName = normalize(this.labels.categories[cat.id] || '');
        cat.emojis.forEach(e => {
          if (normalize(e.n).indexOf(q) > -1 || (catName && catName.indexOf(q) > -1)) {
            out.push(e);
          }
        });
      });
      this.visible = out;
    } else if (this.tab === 'recent') {
      this.visible = this.recent;
    } else {
      const cat = this.categories.find(c => c.id === this.tab);
      this.visible = cat ? cat.emojis : [];
    }
    this.focusIndex = 0;
  }

  /* ---- recentes (localStorage, nunca falha) ---- */

  private readRecent(): ChatEmoji[] {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      const chars: string[] = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(chars)) {
        return [];
      }
      const all: { [c: string]: ChatEmoji } = {};
      this.categories.forEach(cat => cat.emojis.forEach(e => all[e.c] = e));
      return chars.map(c => all[c]).filter(e => !!e).slice(0, RECENT_MAX);
    } catch (e) {
      return [];
    }
  }

  private rememberRecent(emoji: ChatEmoji): void {
    this.recent = [emoji].concat(this.recent.filter(e => e.c !== emoji.c)).slice(0, RECENT_MAX);
    if (this.tabs[0] !== 'recent') {
      const withRecent: ChatEmojiTab[] = ['recent'];
      this.tabs = withRecent.concat(this.tabs);
    }
    if (this.tab === 'recent' && !this.query) {
      this.visible = this.recent;
    }
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.recent.map(e => e.c)));
    } catch (e) {
      /* sem storage (modo privado, quota): os recentes vivem só nesta sessão */
    }
  }
}

/** Minúsculas e sem acentos; local para o componente não depender de `chat.format.ts`. */
function normalize(text: string): string {
  const lower = String(text || '').toLowerCase();
  try {
    return lower.normalize('NFD').replace(/[̀-ͯ]/g, '');
  } catch (e) {
    return lower;
  }
}
